/**
 * grafiekConfig.js — de grafiek bij een vraag (vragen.grafiek_config, migratie 071).
 *
 * Een grafiek is een media-blok, net als een afbeelding: hij kan bij elk
 * vraagtype staan (meerkeuze "welke grafiek…", getal "lees f(3) af", open
 * "verklaar de vorm"). Het antwoord zelf gaat via het vraagtype; het
 * antwoorden ín de grafiek (punten, lijn) komt in een volgende stap.
 *
 * Vorm (v1):
 *   { v: 1,
 *     assen: { xmin, xmax, ymin, ymax, stap, raster, labelX, labelY },
 *     functies: [{ expr, kleur, label }],
 *     punten:   [{ x, y, label }],
 *     lijnen:   [{ door: [[x1, y1], [x2, y2]], label }] }
 */

export const GRAFIEK_KLEUREN = ['#0077b6', '#dc2626', '#16a34a', '#7c3aed', '#ea580c', '#0f766e'];
const MAX_ITEMS = 20;

const getal = (v, standaard) => {
  if (v == null || String(v).trim() === '') return standaard;
  const n = Number(String(v).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : standaard;
};

export function legeGrafiekConfig() {
  return {
    v: 1,
    assen: { xmin: -10, xmax: 10, ymin: -10, ymax: 10, stap: 1, raster: true, labelX: 'x', labelY: 'y' },
    functies: [],
    punten: [],
    lijnen: [],
  };
}

/** Maakt van elke invoer een geldige config (of null als er niets te tonen is). */
export function normaliseerGrafiekConfig(cfg) {
  if (!cfg || typeof cfg !== 'object') return null;
  const leeg = legeGrafiekConfig();
  const a = cfg.assen && typeof cfg.assen === 'object' ? cfg.assen : {};
  let xmin = getal(a.xmin, leeg.assen.xmin);
  let xmax = getal(a.xmax, leeg.assen.xmax);
  let ymin = getal(a.ymin, leeg.assen.ymin);
  let ymax = getal(a.ymax, leeg.assen.ymax);
  if (xmax <= xmin) { xmin = leeg.assen.xmin; xmax = leeg.assen.xmax; }
  if (ymax <= ymin) { ymin = leeg.assen.ymin; ymax = leeg.assen.ymax; }
  const stap = Math.max(0.1, getal(a.stap, leeg.assen.stap));
  const assen = {
    xmin, xmax, ymin, ymax, stap,
    raster: a.raster !== false,
    labelX: String(a.labelX ?? 'x').slice(0, 20),
    labelY: String(a.labelY ?? 'y').slice(0, 20),
  };
  const functies = (Array.isArray(cfg.functies) ? cfg.functies : []).slice(0, MAX_ITEMS)
    .map((f, i) => ({
      expr: String(f?.expr ?? '').slice(0, 200),
      kleur: typeof f?.kleur === 'string' && /^#[0-9a-f]{6}$/i.test(f.kleur) ? f.kleur : GRAFIEK_KLEUREN[i % GRAFIEK_KLEUREN.length],
      label: String(f?.label ?? '').slice(0, 30),
    }))
    .filter(f => f.expr.trim());
  const punten = (Array.isArray(cfg.punten) ? cfg.punten : []).slice(0, MAX_ITEMS)
    .map(p => ({ x: getal(p?.x, NaN), y: getal(p?.y, NaN), label: String(p?.label ?? '').slice(0, 30) }))
    .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y));
  const lijnen = (Array.isArray(cfg.lijnen) ? cfg.lijnen : []).slice(0, MAX_ITEMS)
    .map(l => {
      const d = Array.isArray(l?.door) ? l.door : [];
      const p1 = Array.isArray(d[0]) ? [getal(d[0][0], NaN), getal(d[0][1], NaN)] : [NaN, NaN];
      const p2 = Array.isArray(d[1]) ? [getal(d[1][0], NaN), getal(d[1][1], NaN)] : [NaN, NaN];
      return { door: [p1, p2], label: String(l?.label ?? '').slice(0, 30) };
    })
    .filter(l => l.door.flat().every(Number.isFinite) && (l.door[0][0] !== l.door[1][0] || l.door[0][1] !== l.door[1][1]));
  return { v: 1, assen, functies, punten, lijnen };
}

/** Heeft deze vraag een grafiek om te tonen? */
export function heeftGrafiek(vraag) {
  const cfg = vraag && (vraag.grafiekConfig || vraag.grafiek_config);
  return !!(cfg && typeof cfg === 'object' && cfg.assen);
}
